









import { Component, Mixins, Prop } from "vue-property-decorator";
import { EditingDocument } from "../..";
import { DomainRegistry } from "../../../../../domain";
import { ValueReferenceParameter } from "../../../../../domain/collaboration";
import { IConfiguration } from "../../../../../domain/configuration";
import { EditPhaseKinds } from "../../../../../domain/document";
import { HisInformation, IHisInformationAccessible } from "../../../../../domain/his";
import { PatientProfileInformation } from "../../../../../domain/patient";
import { HisInformationProcessResultConverter } from "../../../../his/components/helper";
import { MenuPane } from "../../../../panes";
import { IButtonConfig } from "../../../../system";
import { ModelEditableMixin, ModalPresentableMixin } from "../../../../system/mixins";
import { PatientProfileApplicator } from "../../helpers";

enum HisInformationUpdateConfirmResult {
  Cancel = 1,
  All = 2,
  EmptyOnly = 3,
}

@Component({
  components: {
    MenuPane,
  }
})
export default class HisInformationMenu extends Mixins(ModelEditableMixin, ModalPresentableMixin) {
  data(): any {
    return {
      isLoading: false,
    };
  }
  isLoading: boolean;

  @Prop()
  hisInformationAccessor: IHisInformationAccessible;

  get editingDocument(): EditingDocument {
    return this.editingSource;
  }

  async reload(): Promise<void> {
    const confirmResult = await this.confirmToReload();
    if (!confirmResult || confirmResult === HisInformationUpdateConfirmResult.Cancel) { return; }

    const shouldUpdateEmptyObjectOnly = (confirmResult === HisInformationUpdateConfirmResult.EmptyOnly);
    await this.executeReload(shouldUpdateEmptyObjectOnly);
  }

  private async confirmToReload(): Promise<HisInformationUpdateConfirmResult> {
    this.beginModal();
    try {
      const index = await this.$commonDialogs.confirmation.show({
        title: "患者情報の更新", 
        message: ["患者情報を再取得し、内容を更新してもよろしいですか？"],
        buttons: this.createButtons(),
        cancellation: {
          isEnable: true,
        },
      });
      return this.toHisInformationUpdateConfirmResult(index);
    } catch (e) {
      return HisInformationUpdateConfirmResult.Cancel;
    } finally {
      this.endModal();
    }
  }

  private createButtons(): (string | IButtonConfig)[] {
    return this.shouldShowUpdateEmptyObjectOnlyButton
      ? [
          {
            kind: "confirm",
            label: "すべての項目を更新する",
          }, 
          {
            kind: "confirm",
            label: "空白の項目のみ更新する",
          }, 
        ]
      : [
        {
          kind: "confirm",
          label: "更新する",
        }, 
      ];
  }

  private toHisInformationUpdateConfirmResult(index: number): HisInformationUpdateConfirmResult {
    if (index === 0) { return HisInformationUpdateConfirmResult.All; }
    if (index === 1 && this.shouldShowUpdateEmptyObjectOnlyButton) { return HisInformationUpdateConfirmResult.EmptyOnly; }
    // キャンセルボタンをクリック時はnull
    return HisInformationUpdateConfirmResult.Cancel;
  }

  private get shouldShowUpdateEmptyObjectOnlyButton(): boolean {
    return this.$configuration.behavior.document.editor.hisInformationUpdateMenu.shouldShowUpdateEmptyObjectOnlyButton;
  }

  private async executeReload(shouldUpdateEmptyObjectOnly: boolean): Promise<void> {
    this.isLoading = true;
    try {
      const parameter = await this.getDocumentValueReferenceParameter();
      const info = await this.getLatestHisInformation(this.editingDocument.attributes.patient.id, parameter);
      const patientProfile = await this.getLatestPatientProfile(this.editingDocument.attributes.patient.id);
      
      // writePropertyを連続して実行すると反映漏れが発生するため、一回で反映させている
      this.writeProperty((s: EditingDocument) => {
        s.setHisInformation(info, shouldUpdateEmptyObjectOnly);
        if (patientProfile) {
          // V1では患者プロフィールをUIから編集したときは文書データは更新しない？ためpatientProfileのsetでは文書データに適用しないようにしている模様
          // 自前で文書データに適用する
          const patientProfileApplicator = new PatientProfileApplicator(s);
          patientProfileApplicator.apply(patientProfile);
          s.patientProfile = patientProfile;
        }
      });
      const hasHisInformationAlerts = this.showHisInformationAlerts();

      // V1では患者情報更新ボタンをクリックしたタイミングで外部データの自動設定「する」のオブジェクトも処理していた模様
      // 理解に苦しむ挙動ではあるが、三重大で必要となったため対応している
      if (this.$configuration.behavior.document.editor.mischievousObjects.externalData.shouldExeucuteWhenHisInformationManualUpdate) {
        try {
          await this.$nextTick();
          this.$logger.info("自動設定する指定となっている外部データを処理します。");
          await this.editingDocument.executeScriptInDocumentAsync(EditPhaseKinds.PatientInformationManualUpdate);
          await this.$nextTick();
        } catch (e) {
          this.$error("外部データ", "外部データの実行中にエラーが発生しています。", e);
        }
      }

      // 必要であれば外部処理をキック
      const intervalMilliSeconds = (this.$store.getters.configuration as IConfiguration).behavior.document.editor.externalScript.executeIntervalMilliSeconds;
      if (intervalMilliSeconds <= 0) {
        // 定期実行タイマーにより外部処理がキックされないので、自分で外部処理をキックする必要がある
        this.$logger.debug("HIS情報の更新後の外部処理を実行します。");
        await this.$nextTick();
        const isSuccess = await this.editingDocument.executeStackedScriptsAsyncWithResult();
        if (!isSuccess) {
          this.$error(
            "外部スクリプト",
            "外部スクリプトの実行中にエラーが発生しています。",
          );
        }
      }
      if (hasHisInformationAlerts) {
        this.$warn(
          "更新結果",
          "患者関連情報を更新しました。一部の情報を取得できませんでした。",
        );
      } else {
        this.$success(
          "更新成功",
          "患者関連情報を更新しました。",
        );
      }
    } catch (e) {
      this.$error(
        "患者関連情報取得失敗",
        "患者関連情報を取得できませんでした。",
        e,
      );
    } finally {
      this.isLoading = false;
    }
  }
  private async getDocumentValueReferenceParameter(): Promise<ValueReferenceParameter> {
    if (this.editingDocument.source.documentId == null) {
      // 新規記載時は情報が揃っている
      const parameter = new ValueReferenceParameter();
      parameter.institutionNo = this.editingDocument.attributes.institutionNo;
      parameter.regionalInstitutionNo = this.editingDocument.attributes.regionalInstitutionNo;
      parameter.hisInfoGetterParameter = this.editingDocument.attributes.hisInfoGetterParameter;
      parameter.systemKey = this.editingDocument.attributes.systemKey;
      parameter.linkKey = this.editingDocument.attributes.linkKey;
      return parameter;
    } else {
      // 編集時はバックエンドから取得する
      const repo = DomainRegistry.collaboration.getLinkedDocumentRepository();
      return repo.getValueReferenceParameter(this.editingDocument.source.documentId);
    }
  }
  
  private async getLatestHisInformation(patientId: string, parameter: ValueReferenceParameter): Promise<HisInformation> {
      if (this.hisInformationAccessor) {
        return this.hisInformationAccessor.getLatestHisInformation(patientId, parameter);
      }
      const repo = DomainRegistry.his.getHisInformationRepository();
      return repo.getLatestHisInformation(
        patientId,
        parameter ? parameter.institutionNo : "",
        parameter ? parameter.regionalInstitutionNo : "",
        parameter ? parameter.hisInfoGetterParameter : "",
        parameter ? parameter.systemKey : "",
        parameter ? parameter.linkKey : "");
  }

  private async getLatestPatientProfile(patientId: string): Promise<PatientProfileInformation> {
    if (!this.$configuration.behavior.document.editor.patientProfile.isEnable) { return null; }
    try {
      const repo = DomainRegistry.patient.getPatientProfileRepository();
      return await repo.getLatestPatientProfileInformation(patientId);
    } catch (e) {
      this.$warn("患者プロフィール", "患者プロフィールの取得に失敗しました。", e);
      return null;
    }
  }

  private showHisInformationAlerts(): boolean {
    try {
      const message = HisInformationProcessResultConverter.toMessageText(this.editingDocument.source.hisInformation.processResult);
      if (!message) { return false; }
      this.$warn("患者情報取得", message);
    } catch (e) {
      // エラーは起こさないようにしているが念のため
      this.$logger.error("情報取得失敗の通知処理中にエラーが発生しました。");
      this.$logger.error(e);
    }
    return true;
  }
}
